/* Wellrobed product card actions for Shopify Horizon.
 * Uses native Horizon cart events so cart drawer/count can refresh without a page reload.
 * Wishlist uses the same localStorage key as the prior Wellrobed wishlist package.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const WISHLIST_KEY = 'wellrobed-wishlist-v1';

function readWishlist() {
  try {
    const items = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(items) ? items : [];
  } catch (_) { return []; }
}
function saveWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('wellrobed:wishlist-changed'));
    return true;
  } catch (_) { return false; }
}
function savedProduct(extras) {
  return {
    handle: extras.dataset.wrHandle,
    title: extras.dataset.wrTitle,
    url: extras.dataset.wrUrl,
    image: extras.dataset.wrImage,
    price: extras.dataset.wrPrice
  };
}
function syncHearts() {
  const selected = new Set(readWishlist().map(item => item.handle));
  document.querySelectorAll('[data-wr-card-extras]').forEach(extras => {
    const heart = extras.querySelector('[data-wr-wishlist]') ||
      extras.closest('product-card')?.querySelector('[data-wr-wishlist]');
    if (!heart) return;
    const isSaved = selected.has(extras.dataset.wrHandle);
    heart.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    heart.setAttribute('aria-label', `${isSaved ? 'Remove' : 'Add'} ${extras.dataset.wrTitle} ${isSaved ? 'from' : 'to'} wishlist`);
    heart.title = isSaved ? 'Remove from wishlist' : 'Save to wishlist';
  });
}
function variantData(extras) {
  try { return JSON.parse(extras.querySelector('[data-wr-variants]')?.textContent || '[]'); }
  catch (_) { return []; }
}
function getSelectedOptions(card, extras, variants) {
  let options;
  try { options = JSON.parse(extras.dataset.wrSelectedOptions || '[]'); }
  catch (_) { options = []; }
  let variantId = '';
  try { variantId = String(card.getSelectedVariantId?.() || ''); }
  catch (_) { /* The card may be an older Horizon version. */ }
  if (!variantId) {
    const href = card.querySelector('a.product-card__link')?.href;
    if (href) {
      try { variantId = new URL(href, location.origin).searchParams.get('variant') || ''; }
      catch (_) { /* Ignore malformed URL. */ }
    }
  }
  const selectedVariant = variants.find(v => String(v.id) === variantId);
  return selectedVariant?.options || options;
}
function updateSizes(extras) {
  const card = extras.closest('product-card');
  if (!card) return;
  const variants = variantData(extras);
  const sizeIndex = Number(extras.dataset.wrSizeIndex);
  if (sizeIndex < 0) return;
  const selected = getSelectedOptions(card, extras, variants);
  extras.querySelectorAll('[data-wr-size]').forEach(button => {
    const label = button.dataset.wrSize;
    const variant = variants.find(v =>
      v.options[sizeIndex] === label &&
      v.options.every((option, index) => index === sizeIndex || option === selected[index])
    );
    button.dataset.wrVariantId = variant?.id || '';
    button.dataset.wrQuantity = variant?.quantity || '1';
    button.disabled = !variant?.available;
    if (button.disabled) button.title = `${label} sold out in the selected color`;
    else button.title = `Add size ${label} to bag`;
  });
}
function initCard(extras) {
  if (extras.dataset.wrReady === 'yes') return;
  const card = extras.closest('product-card');
  if (!card) return;
  extras.dataset.wrReady = 'yes';

  const panel = extras.querySelector('[data-wr-quick-add]');
  const gallery = card.querySelector('.card-gallery');
  if (panel && gallery) {
    gallery.appendChild(panel);
    panel.classList.add('wr-card-quick-add--mounted');

    // Only touch devices show this button; desktop uses hover-to-reveal.
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'wr-card-quick-add__mobile-toggle';
    toggle.setAttribute('data-wr-mobile-toggle', '');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Show available sizes for ' + (extras.dataset.wrTitle || 'product'));
    toggle.textContent = 'Add to Bag';
    const uniqueId = 'wr-card-panel-' + extras.dataset.wrProductId + '-' +
      [...document.querySelectorAll('[data-wr-card-extras]')].indexOf(extras);
    panel.id = uniqueId;
    toggle.setAttribute('aria-controls', uniqueId);
    gallery.appendChild(toggle);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'wr-card-quick-add__mobile-close';
    close.setAttribute('data-wr-mobile-close', '');
    close.setAttribute('aria-label', 'Close size selector');
    close.textContent = '×';
    panel.appendChild(close);
  }

  const heart = extras.querySelector('[data-wr-wishlist]');
  const titleAnchor = card.querySelector('a[ref="productTitleLink"]');
  if (heart && titleAnchor && !titleAnchor.closest('.wr-card-title-row')) {
    const titleRow = document.createElement('div');
    titleRow.className = 'wr-card-title-row';
    titleAnchor.parentElement.insertBefore(titleRow, titleAnchor);
    titleRow.append(titleAnchor, heart);
  } else if (heart) {
    // Fallback for a customized product-card title block.
    const galleryParent = gallery?.parentElement || card.querySelector('.product-card__content');
    if (galleryParent) galleryParent.appendChild(heart);
  }
  updateSizes(extras);
}
function initAll() {
  document.querySelectorAll('[data-wr-card-extras]').forEach(initCard);
  syncHearts();
}
function showStatus(extras, message) {
  const container = extras.closest('product-card');
  const status = container?.querySelector('[data-wr-status]');
  if (status) status.textContent = message;
}
async function addToCart(button, extras, variantId, quantity = 1) {
  if (!variantId || button.disabled) return;
  const card = extras.closest('product-card');
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  showStatus(extras, 'Adding to bag…');

  const deferred = CartLinesUpdateEvent.createPromise();
  card.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variantId), quantity }],
    promise: deferred.promise
  }));

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart/add.js`;
  const cartItemsSections = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map(el => el.dataset.sectionId).filter(Boolean);
  try {
    const response = await fetch(cartUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: cartItemsSections.join(',')
      })
    });
    const added = await response.json();
    if (!response.ok || added.status) {
      throw new Error(added.description || added.message || 'Could not add this size to the bag.');
    }

    const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
    if (!cartResponse.ok) throw new Error('Cart was updated, but its display could not refresh.');
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'wellrobed-card',
        sourceId: `wr-product-${extras.dataset.wrProductId}`,
        itemCount: quantity,
        productId: extras.dataset.wrProductId,
        sections: added.sections,
        didError: false
      }
    });
    showStatus(extras, 'Added to bag');
    button.setAttribute('data-wr-added', 'true');
    setTimeout(() => button.removeAttribute('data-wr-added'), 1300);
  } catch (error) {
    // Resolve the native event even on errors, so Horizon listeners do not hang.
    try {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
      const cart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'wellrobed-card' }
      });
    } catch (refreshError) {
      deferred.reject(refreshError);
    }
    card.dispatchEvent(new CartErrorEvent({ error: error.message || 'Unable to add to bag', code: 'INVALID' }));
    showStatus(extras, error.message || 'Unable to add to bag');
    // The error is announced by the aria-live status above.
  } finally {
    button.removeAttribute('aria-busy');
    updateSizes(extras);
    if (!button.matches('[data-wr-size]')) button.disabled = false;
  }
}

document.addEventListener('click', event => {
  const button = event.target.closest('[data-wr-wishlist], [data-wr-size], [data-wr-add-default], [data-wr-mobile-toggle], [data-wr-mobile-close]');
  if (!button) return;
  const card = button.closest('product-card');
  const extras = card?.querySelector('[data-wr-card-extras]');
  if (!extras) return;
  event.preventDefault();
  event.stopPropagation();
  if (button.matches('[data-wr-mobile-toggle], [data-wr-mobile-close]')) {
    const panel = card.querySelector('[data-wr-quick-add]');
    const toggle = card.querySelector('[data-wr-mobile-toggle]');
    if (!panel || !toggle) return;
    const isOpen = button.matches('[data-wr-mobile-toggle]') && !panel.classList.contains('wr-card-quick-add--open');
    panel.classList.toggle('wr-card-quick-add--open', isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
    if (isOpen) panel.querySelector('[data-wr-size]:not(:disabled), [data-wr-add-default], .wr-card-quick-add__options')?.focus({ preventScroll: true });
    else toggle.focus({ preventScroll: true });
    return;
  }
  if (button.matches('[data-wr-wishlist]')) {
    const product = savedProduct(extras);
    const existing = readWishlist();
    const updated = existing.some(item => item.handle === product.handle)
      ? existing.filter(item => item.handle !== product.handle)
      : [...existing, product];
    if (!saveWishlist(updated)) showStatus(extras, 'Could not save wishlist in this browser');
    syncHearts();
    return;
  }
  if (button.matches('[data-wr-size]')) {
    addToCart(button, extras, button.dataset.wrVariantId, Number(button.dataset.wrQuantity) || 1);
  } else {
    addToCart(button, extras, button.dataset.wrAddDefault, 1);
  }
}, true);

document.addEventListener('change', event => {
  const card = event.target.closest?.('product-card');
  const extras = card?.querySelector('[data-wr-card-extras]');
  if (extras) setTimeout(() => updateSizes(extras), 120);
});
document.addEventListener('shopify:section:load', initAll);
window.addEventListener('wellrobed:wishlist-changed', syncHearts);
window.addEventListener('storage', event => { if (event.key === WISHLIST_KEY) syncHearts(); });

// Collection filtering and pagination may replace cards without a full page refresh.
let queued = false;
const observer = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => { queued = false; initAll(); });
});
observer.observe(document.documentElement, { childList: true, subtree: true });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();

// Wellrobed product-card button zoom
(() => {
  let dialog;
  let stage;
  let image;
  let opener;

  const ensureDialog = () => {
    if (dialog) return;

    dialog = document.createElement('dialog');
    dialog.className = 'wr-card-zoom-dialog';
    dialog.setAttribute('aria-label', 'Enlarged product image');
    dialog.innerHTML = `
      <button type="button" class="wr-card-zoom-dialog__close" aria-label="Close image zoom">&times;</button>
      <button type="button" class="wr-card-zoom-dialog__stage" aria-label="Magnify product image">
        <img class="wr-card-zoom-dialog__image" alt="">
      </button>
    `;
    document.body.appendChild(dialog);

    stage = dialog.querySelector('.wr-card-zoom-dialog__stage');
    image = dialog.querySelector('.wr-card-zoom-dialog__image');

    dialog.querySelector('.wr-card-zoom-dialog__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    stage.addEventListener('click', (event) => {
      const zoomed = stage.classList.toggle('is-zoomed');
      stage.setAttribute('aria-label', zoomed ? 'Reduce image zoom' : 'Magnify product image');
      if (zoomed) {
        const rect = stage.getBoundingClientRect();
        const x = event.detail === 0 ? 50 : Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        const y = event.detail === 0 ? 50 : Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        image.style.transformOrigin = `${x}% ${y}%`;
      }
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('wr-card-zoom-open');
      image.removeAttribute('src');
      stage.classList.remove('is-zoomed');
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = null;
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-wr-card-zoom-trigger]');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const gallery = button.closest('product-card .card-gallery');
    if (!gallery) return;
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] .product-media__image') ||
      gallery.querySelector('slideshow-slide:not([hidden]) .product-media__image') ||
      gallery.querySelector('.product-media__image');
    const src =
      activeImage?.getAttribute('data-max-resolution') ||
      button.getAttribute('data-wr-zoom-src') ||
      activeImage?.currentSrc ||
      activeImage?.src;
    if (!src) return;

    ensureDialog();
    opener = button;
    stage.classList.remove('is-zoomed');
    stage.setAttribute('aria-label', 'Magnify product image');
    image.style.transformOrigin = 'center center';
    image.alt = activeImage?.alt || button.getAttribute('data-wr-zoom-title') || 'Product image';
    image.src = src;
    document.documentElement.classList.add('wr-card-zoom-open');
    dialog.showModal();
  }, true);
})();
